import React from 'react';
import { Pressable, SectionList, StyleSheet, Text, View } from 'react-native';
import { useRouter } from 'expo-router';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { markAlertRead, useAlerts } from '../../src/data';
import { clock, dayLabel, won } from '../../src/format';
import { Empty, Head, Tag } from '../../src/ui';
import { D } from '../../src/theme';
import type { AlertDoc } from '../../src/types';

export default function Alerts() {
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const alerts = useAlerts();
  const sections: { title: string; data: AlertDoc[] }[] = [];
  for (const a of alerts) {
    const t = dayLabel(a.created_at);
    const last = sections[sections.length - 1];
    if (last && last.title === t) last.data.push(a);
    else sections.push({ title: t, data: [a] });
  }

  function open(a: AlertDoc) {
    if (!a.read) markAlertRead(a.id).catch(() => {});
    router.push(`/deal/${a.deal_id}`);
  }

  return (
    <View style={{ flex: 1, backgroundColor: D.ground }}>
      <View style={{ paddingTop: insets.top + 14 }}>
        <Head kicker="조건에 맞는 딜이 올라오면 여기에" title="알림" />
      </View>
      <SectionList
        sections={sections}
        keyExtractor={(a) => a.id}
        stickySectionHeadersEnabled={false}
        contentContainerStyle={{ paddingHorizontal: 16, paddingTop: 4, paddingBottom: 24, gap: 8 }}
        renderSectionHeader={({ section }) => <Text style={s.day}>{section.title}</Text>}
        ListFooterComponent={
          <Text style={s.disclosure}>이 서비스는 제휴마케팅이 포함된 광고로 커미션을 지급 받으며 상품 가격에는 영향을 주지 않습니다.</Text>
        }
        renderItem={({ item: a }) => (
          <Pressable onPress={() => open(a)} style={({ pressed }) => [s.card, !a.read && s.unread, pressed && { opacity: 0.9 }]}>
            <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 7, flex: 1, minWidth: 0 }}>
                {!a.read && <View style={s.dot} />}
                <Text style={s.cond} numberOfLines={1}>{`"${a.condition_text}"`}</Text>
              </View>
              <Text style={s.clock}>{clock(a.created_at)}</Text>
            </View>
            <Text style={s.title} numberOfLines={2}>{a.product}</Text>
            <View style={{ flexDirection: 'row', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
              <Text style={s.price}>{a.effective_price != null ? won(a.effective_price) : '가격 미확인'}</Text>
              {a.reasons[0] ? <Text style={s.why}>{a.reasons[0]}</Text> : null}
            </View>
            <View style={{ flexDirection: 'row', gap: 5, flexWrap: 'wrap' }}>
              <Tag label={`${Math.round(a.score)}점`} />
              {a.mall ? <Tag label={a.mall} tone="quiet" /> : null}
              <Tag label={`신뢰도 ${Math.round(a.confidence * 100)}%`} tone="quiet" />
            </View>
          </Pressable>
        )}
        ListEmptyComponent={
          <Empty title="아직 알림이 없어요" body="내 조건에 맞는 딜이 올라오면 여기 쌓여요." />
        }
      />
    </View>
  );
}

const s = StyleSheet.create({
  disclosure: { fontSize: 10.5, color: D.ink2, lineHeight: 16, marginTop: 16 },
  day: { fontSize: 10, fontWeight: '800', color: D.ink3, letterSpacing: 0.8, marginTop: 8, marginBottom: 2 },
  card: { backgroundColor: D.panel, borderWidth: 1, borderColor: D.ink, padding: 14, gap: 9 },
  unread: { backgroundColor: '#FBFFCE' },
  dot: { width: 7, height: 7, backgroundColor: D.alert },
  clock: { fontSize: 11, fontWeight: '700', color: D.ink3, fontVariant: ['tabular-nums'] },
  cond: { fontSize: 12, fontWeight: '800', color: D.ink2, flexShrink: 1 },
  title: { fontSize: 15.5, fontWeight: '800', lineHeight: 21, color: D.ink },
  price: { fontSize: 19, fontWeight: '900', color: D.ink, letterSpacing: -0.5, fontVariant: ['tabular-nums'] },
  why: { fontSize: 12.5, fontWeight: '800', color: D.alert },
});
